"use client"

import * as React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { Button } from '@open-mercato/ui/primitives/button'
import { Spinner } from '@open-mercato/ui/primitives/spinner'
import { Badge } from '@open-mercato/ui/primitives/badge'

export type DriverProfile = {
  id: string
  firstName: string
  lastName: string
  phone: string | null
  email: string | null
  userId: string | null
}

export type DriverStatusAction = {
  status: string
  label: string
}

export type DriverPhoto = {
  id: string
  fileName: string
  fileSize: number
  mimeType: string | null
  url: string
  thumbnailUrl: string
  createdAt: string
}

export type DriverOrder = {
  id: string
  orderNumber: string
  status: string
  version: number
  plannedPickupAt: string | null
  plannedDeliveryAt: string | null
  actualPickupAt: string | null
  actualDeliveryAt: string | null
  vehiclePlate: string | null
  originLabel: string | null
  destinationLabel: string | null
  dispatcherNotes: string | null
  attachmentCount: number
  ecmr: {
    id: string
    documentNumber: string
    status: string
    sealHash: string
  } | null
}

export type DriverActiveOrder = DriverOrder & {
  statusActions: DriverStatusAction[]
  photos: DriverPhoto[]
}

export type DriverOrdersResponse = {
  activeDispatchId: string | null
  items: DriverOrder[]
}

export type DriverQrArtifact = {
  documentNumber: string
  status: string
  sealHash: string
  content: string
  svg: string
}

function toErrorMessage(payload: unknown, fallback: string): string {
  if (!payload) return fallback
  if (typeof payload === 'string') return payload
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const nested = toErrorMessage(item, '')
      if (nested) return nested
    }
    return fallback
  }
  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>
    return (
      toErrorMessage(record.error, '')
      || toErrorMessage(record.message, '')
      || toErrorMessage(record.detail, '')
      || fallback
    )
  }
  return fallback
}

async function fetchOrThrow<T>(url: string, init: RequestInit | undefined, fallback: string): Promise<T> {
  const call = await apiCall<T>(url, init)
  if (!call.ok || call.result == null) {
    throw new Error(toErrorMessage(call.result, fallback))
  }
  return call.result
}

export function fetchDriverProfile() {
  return fetchOrThrow<DriverProfile>('/api/road/driver/me', undefined, 'Failed to load driver profile.')
}

export function fetchDriverOrders() {
  return fetchOrThrow<DriverOrdersResponse>('/api/road/driver/orders', undefined, 'Failed to load driver orders.')
}

export function fetchDriverActiveOrder() {
  return fetchOrThrow<DriverActiveOrder | null>('/api/road/driver/active', undefined, 'Failed to load active order.')
}

export function fetchDriverQrArtifact() {
  return fetchOrThrow<DriverQrArtifact>('/api/road/driver/active/qr', undefined, 'Failed to load eCMR QR.')
}

export function formatDriverDateTime(value: string | null | undefined): string {
  if (!value) return 'Not set'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Not set'
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

export function formatDriverFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DriverStatusBadge({ status }: { status: string }) {
  const variant =
    status === 'COMPLETED'
      ? 'default'
      : status === 'CANCELLED'
        ? 'destructive'
        : 'secondary'
  return (
    <Badge variant={variant} className="rounded-full px-3 py-1 text-[11px] tracking-[0.16em] uppercase">
      {status.replaceAll('_', ' ')}
    </Badge>
  )
}

export function DriverPwaShell(props: {
  title: string
  subtitle: string
  profile: DriverProfile | null
  children: React.ReactNode
}) {
  const pathname = usePathname()
  const tabs = [
    { href: '/driver/orders', label: 'My Orders' },
    { href: '/driver/active', label: 'Active Order' },
  ]

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.16),transparent_32%),linear-gradient(180deg,#0f172a_0%,#111827_48%,#1f2937_100%)] text-slate-50">
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-4 pb-10 pt-6">
        <header className="rounded-[28px] border border-white/10 bg-white/8 px-5 py-5 shadow-[0_24px_80px_rgba(2,6,23,0.4)] backdrop-blur">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-300">Driver Workspace</div>
              <h1 className="mt-2 text-2xl font-semibold leading-tight">{props.title}</h1>
              <p className="mt-2 text-sm leading-6 text-slate-300">{props.subtitle}</p>
            </div>
            <div className="rounded-2xl border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sky-200">Signed In</div>
              <div className="mt-1 text-sm font-medium">
                {props.profile ? `${props.profile.firstName} ${props.profile.lastName}` : 'Driver'}
              </div>
            </div>
          </div>
          <nav className="mt-5 grid grid-cols-2 gap-2 rounded-2xl bg-slate-950/40 p-1">
            {tabs.map((tab) => {
              const active = pathname === tab.href
              return (
                <Button
                  key={tab.href}
                  asChild
                  variant={active ? 'default' : 'ghost'}
                  className={active ? 'rounded-2xl bg-sky-500 text-slate-950 hover:bg-sky-400' : 'rounded-2xl text-slate-200 hover:bg-white/8'}
                >
                  <Link href={tab.href}>{tab.label}</Link>
                </Button>
              )
            })}
          </nav>
        </header>

        <main className="mt-4 flex flex-1 flex-col gap-4">{props.children}</main>
      </div>
    </div>
  )
}

export function DriverPanel(props: {
  title: string
  eyebrow?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="rounded-[28px] border border-white/10 bg-slate-950/50 px-5 py-5 shadow-[0_18px_56px_rgba(2,6,23,0.34)] backdrop-blur">
      <div className="flex items-start justify-between gap-4">
        <div>
          {props.eyebrow ? (
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">{props.eyebrow}</div>
          ) : null}
          <h2 className="mt-1 text-lg font-semibold text-slate-50">{props.title}</h2>
        </div>
        {props.action}
      </div>
      <div className="mt-4">{props.children}</div>
    </section>
  )
}

export function DriverLoadingState() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-50">
      <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/6 px-5 py-4">
        <Spinner />
        <span className="text-sm text-slate-200">Loading driver workspace…</span>
      </div>
    </div>
  )
}

export function DriverErrorState(props: { title: string; description: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 text-slate-50">
      <div className="w-full max-w-md rounded-[28px] border border-rose-400/20 bg-rose-500/8 px-6 py-6 shadow-[0_22px_70px_rgba(2,6,23,0.42)]">
        <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-200">Access</div>
        <h1 className="mt-2 text-xl font-semibold">{props.title}</h1>
        <p className="mt-3 text-sm leading-6 text-rose-100/90">{props.description}</p>
      </div>
    </div>
  )
}

export function DriverInlineMessage(props: { tone: 'info' | 'success' | 'error'; text: string }) {
  const styles =
    props.tone === 'success'
      ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-100'
      : props.tone === 'error'
        ? 'border-rose-400/25 bg-rose-500/10 text-rose-100'
        : 'border-sky-400/25 bg-sky-500/10 text-sky-100'
  return <div className={`rounded-2xl border px-4 py-3 text-sm ${styles}`}>{props.text}</div>
}
